import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Box,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { subscribeToBaaRequests } from "services/baaAdmin";
import {
  FILTER_TABS,
  countByFilter,
  matchesFilter,
  statusColor,
  statusLabel,
} from "features/baa/baaStatus";

const ROWS_PER_PAGE = 25;

const toDate = (ts) => {
  if (!ts) return null;
  return typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
};
const formatDate = (ts) => {
  const d = toDate(ts);
  return d ? format(d, "MMM d, yyyy") : "—";
};

export default function BAARequestsList() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("all");
  const [page, setPage] = useState(0);

  useEffect(() => {
    const unsubscribe = subscribeToBaaRequests(
      (list) => {
        setRequests(list);
        setLoading(false);
      },
      (err) => {
        setError(err.message || "Failed to load BAA requests.");
        setLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  const counts = useMemo(() => countByFilter(requests), [requests]);
  const filtered = useMemo(
    () => requests.filter((r) => matchesFilter(r.status, tab)),
    [requests, tab]
  );
  const paged = useMemo(
    () => filtered.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE),
    [filtered, page]
  );

  const handleTabChange = (_e, value) => {
    setTab(value);
    setPage(0);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          BAA requests
        </Typography>
        <Typography color="text.secondary">
          Institutional Business Associate Agreement requests and their signing
          status.
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={handleTabChange}
        sx={{ mb: 2 }}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        {FILTER_TABS.map((t) => (
          <Tab
            key={t.key}
            value={t.key}
            label={
              <Badge
                color="primary"
                badgeContent={counts[t.key]}
                showZero
                sx={{ pr: counts[t.key] > 9 ? 2 : 1.5 }}
              >
                {t.label}
              </Badge>
            }
          />
        ))}
      </Tabs>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Institution</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Signatory</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                    No requests in this view.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paged.map((r) => (
                <TableRow
                  key={r.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => navigate(`/admin/baa/requests/${r.id}`)}
                >
                  <TableCell>{r.institutionName}</TableCell>
                  <TableCell>{r.institutionType}</TableCell>
                  <TableCell>{r.signatoryName}</TableCell>
                  <TableCell>{r.signatoryEmail}</TableCell>
                  <TableCell>{formatDate(r.submittedAt)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={statusLabel(r.status)}
                      color={statusColor(r.status)}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
        />
      </TableContainer>
    </Container>
  );
}
